import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import Config from "../../Config";
import useOrderTracking from "../../../hooks/useOrderTracking";
import { get_paymentId } from "../../../store/Actions/authActions";
import {
  homeStatusConfig,
  liveTrackingView,
  lockerOrders,
  normalizeOrder,
  orderIssue,
  paymentIssue,
  placeLine,
  sortByNextMilestone,
  trackingEnabled,
} from "../../../utils/orders";
import WalletTopup from "../Modals/WalletTopup";
import { Icon, Sprite } from "../Orders/icons";
import { MissedSlotCard, PaymentIssueCard } from "../Orders/OrderBlock";
import ConfirmDialog from "../Orders/ConfirmDialog";
import { editFailed, startOrderEdit } from "../Orders/editOrder";
import "../Orders/orders.css";
import "./homeHero.css";

/**
 * Carte d'accueil sous « Bonjour » — OrderHeroCard, PremiumLockerCard et
 * LockerModal de l'app mobile (app/(app)/home.tsx).
 *
 * Une seule carte, pour la commande en cours la plus proche (même tri que
 * « Mes commandes ») ; la première règle qui s'applique gagne :
 *   aucune commande      → « Planifier la collecte »
 *   paiement échoué      → carte paiement (carte refusée / solde insuffisant)
 *   collecte manquée     → carte « Reprogrammer »
 *   livraison manquée    → idem
 *   sinon                → carte statut, cliquable vers /orders, avec la ligne
 *                          chauffeur les jours de collecte ou de livraison
 * Au-dessus : une carte par commande déposée dans un casier premium.
 *
 * Pas de diapositive « Boutique à proximité » : le web n'a pas de page
 * boutiques où l'envoyer.
 */

const STALE_MS = 30000;
const SUPPORT_PHONE = "tel:+3225880240";

const authHeaders = (authUser) => ({
  uid: authUser?.user_uid,
  client: authUser?.user_client,
  "access-token": authUser?.user_access_token,
});

export function HomeHero({ authUser, cards, dispatch, history, hasCart }) {
  const { t, i18n } = useTranslation("app");
  const lang = i18n.language === "fn" ? "fr" : i18n.language === "du" ? "nl" : "en";

  const [orders, setOrders] = useState(null);
  const [laundryName, setLaundryName] = useState(undefined);
  const [walletBalance, setWalletBalance] = useState(0);
  const [topup, setTopup] = useState(null);
  const [locker, setLocker] = useState(null);
  const [editError, setEditError] = useState(null);
  const fetchedAt = useRef(0);

  const loadOrders = useCallback(() => {
    fetchedAt.current = Date.now();
    return Axios({ url: `${Config.apiURL}/customer/orders`, method: "GET", headers: authHeaders(authUser) })
      .then((response) =>
        setOrders({
          current: sortByNextMilestone((response?.data?.current_orders || []).map(normalizeOrder)),
          past: (response?.data?.past_orders || []).map(normalizeOrder),
        })
      )
      // Sans réponse, pas de carte : mieux que d'annoncer un état faux.
      .catch(() => setOrders((previous) => previous || { failed: true, current: [], past: [] }));
  }, [authUser]);

  useEffect(() => {
    loadOrders();
    Axios({ url: `${Config.apiURL}/customer/laundry_partner`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => {
        const data = response?.data;
        const partner = data?.laundry_partner ?? data?.partner ?? data?.data ?? data;
        setLaundryName(partner?.name || undefined);
      })
      .catch(() => setLaundryName(undefined));

    const refresh = () => {
      if (!document.hidden && Date.now() - fetchedAt.current > STALE_MS) loadOrders();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [authUser, loadOrders]);

  const order = orders?.current?.[0] || null;
  // L'accueil passe le paiement échoué avant le créneau manqué ; « Mes
  // commandes » fait l'inverse (orderIssue). Même ordre que l'app mobile.
  const issue = !order ? null : order.payment_status === "failed" ? "payment" : orderIssue(order);

  useEffect(() => {
    if (issue !== "payment") return;
    Axios({ url: `${Config.apiURL}/customer/wallet`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => setWalletBalance(Number(response?.data?.wallet?.amount) || 0))
      .catch(() => setWalletBalance(0));
    dispatch(get_paymentId());
  }, [issue, authUser, dispatch]);

  const tracking = useOrderTracking(order?.id, Boolean(order) && trackingEnabled(order), authUser);
  const live = liveTrackingView(tracking.data, { failed: tracking.failed }, t);

  if (!orders || orders.failed) return null;

  const resolve = async (target, leg) => {
    try {
      history.push(await dispatch(startOrderEdit(target, leg)));
    } catch (error) {
      setEditError(editFailed(t));
    }
  };

  let hero;
  if (!order) {
    hero = <ScheduleCard t={t} onStart={hasCart ? () => history.push("/checkout/pickup") : null} />;
  } else if (issue === "payment") {
    hero = (
      <PaymentIssueCard
        order={order}
        info={paymentIssue(order, { walletBalance, cards }, t)}
        t={t}
        onTryAnotherCard={() => history.push("/paymentMethod")}
        onTopup={(target, info) => setTopup({ total: info.total, balance: info.balance })}
      />
    );
  } else if (issue === "pickup" || issue === "delivery") {
    hero = <MissedSlotCard order={order} leg={issue} live={live} t={t} lang={lang} onResolve={resolve} />;
  } else {
    hero = <StatusCard order={order} t={t} lang={lang} laundryName={laundryName} live={live} />;
  }

  return (
    <div className="wosh-hh">
      <Sprite />
      {lockerOrders(orders.past).map((item) => (
        <button type="button" className="wosh-hh-locker" key={item.id} onClick={() => setLocker(item)}>
          <span className="wosh-hh-locker__icon">
            <Icon name="lock-open-outline" />
          </span>
          <span className="wosh-hh-locker__body">
            <span className="wosh-hh-locker__eyebrow">{t("premiumLocker.cardEyebrow")}</span>
            <span className="wosh-hh-locker__title">{t("premiumLocker.cardTitle")}</span>
            <span className="wosh-hh-locker__sub">{t("premiumLocker.cardSub")}</span>
          </span>
          <Icon name="chevron-forward" className="wosh-hh-locker__chevron" />
        </button>
      ))}

      {hero}

      {locker && (
        <LockerSheet
          order={locker}
          t={t}
          authUser={authUser}
          onClose={() => setLocker(null)}
          onRetrieved={() => {
            setLocker(null);
            loadOrders();
          }}
        />
      )}

      <WalletTopup open={Boolean(topup)} onClose={() => setTopup(null)} total={topup?.total} balance={topup?.balance} />
      <ConfirmDialog
        open={Boolean(editError)}
        {...editError}
        confirm="OK"
        onCancel={() => setEditError(null)}
        onConfirm={() => setEditError(null)}
      />
    </div>
  );
}

/* ── A. Aucune commande ────────────────────────────────── */

function ScheduleCard({ t, onStart }) {
  const body = (
    <>
      <span className="wosh-hh-new__circle wosh-hh-new__circle--lg" />
      <span className="wosh-hh-new__circle wosh-hh-new__circle--md" />
      <span className="wosh-hh-new__copy">
        <span className="wosh-hh-new__eyebrow">{t("home.newOrder")}</span>
        <span className="wosh-hh-new__title">{t("home.schedulePickup")}</span>
        <span className="wosh-hh-new__sub">{t("home.avgTime")}</span>
      </span>
      <span className="wosh-hh-new__plus" aria-hidden="true">
        +
      </span>
    </>
  );

  /* Sur le web, le catalogue est déjà à côté : la carte ne mène aux créneaux
     qu'une fois le panier rempli. */
  return onStart ? (
    <button type="button" className="wosh-hh-new" onClick={onStart}>
      {body}
    </button>
  ) : (
    <div className="wosh-hh-new">{body}</div>
  );
}

/* ── D + E. Carte statut et ligne chauffeur ────────────── */

function StatusCard({ order, t, lang, laundryName, live }) {
  const config = homeStatusConfig(order, { t, lang, laundryName, live });
  const place = placeLine(order);

  return (
    <Link to="/orders" className="wosh-ord-hero wosh-hh-status">
      <span className="wosh-ord-hero__status">
        <span className="wosh-ord-pulse" />
        <span className="wosh-ord-hero__eyebrow">{config.eyebrow}</span>
      </span>
      <span className="wosh-ord-hero__title">{config.title}</span>
      {config.sub && <span className="wosh-ord-hero__sub">{config.sub}</span>}
      {place && (
        <span className="wosh-ord-hero__place">
          <Icon name="location-outline" />
          <span>{place}</span>
        </span>
      )}
      <HeroLive live={live} t={t} />
    </Link>
  );
}

/**
 * La ligne chauffeur de l'accueil (LiveTracking variant « hero ») : rien sans
 * nom de chauffeur. À l'approche, la ligne verte et le bouton de la carte en
 * direct — qui ouvre « Mes commandes », où la carte est affichée : le web n'a
 * pas encore de page plein écran.
 */
function HeroLive({ live, t }) {
  if (!live.isActive || !live.driverName) return null;

  if (live.phase === "approaching") {
    return (
      <>
        <span className="wosh-ord-hero__divider" />
        <span className="wosh-ord-approach__line">
          <span className="wosh-ord-pulse wosh-ord-pulse--live" />
          {live.copy.sub}
        </span>
        <span className="wosh-hh-map-btn">
          <span className="wosh-hh-map-btn__dot" />
          {t("liveTracking.openLiveMap")}
          <Icon name="chevron-forward" />
        </span>
      </>
    );
  }

  return (
    <>
      <span className="wosh-ord-hero__divider" />
      <span className="wosh-ord-live">
        <span className="wosh-ord-live__tile">
          <Icon name={live.phase === "completed" ? "checkmark-done-outline" : "bus-outline"} />
          <span className="wosh-ord-live__tile-dot" />
        </span>
        <span className="wosh-ord-live__text">
          <span className="wosh-ord-live__title">{live.copy.title}</span>
          {live.copy.sub && <span className="wosh-ord-live__sub">{live.copy.sub}</span>}
        </span>
      </span>
    </>
  );
}

/* ── F. Casier premium ─────────────────────────────────── */

function LockerSheet({ order, t, authUser, onClose, onRetrieved }) {
  const [view, setView] = useState("lockers");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const lockers = order.lockers || [];

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const retrieve = async () => {
    setBusy(true);
    setError(null);
    try {
      await Axios({
        url: `${Config.apiURL}/customer/orders/retrieve/${order.id}`,
        method: "PUT",
        headers: authHeaders(authUser),
        data: { order: { status: "retrieved_by_user" } },
      });
      onRetrieved();
    } catch (err) {
      setError(t("home.genericError"));
      setBusy(false);
    }
  };

  return (
    <div className="wosh-hh-sheet" onClick={onClose}>
      <div
        className="wosh-hh-sheet__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wosh-hh-sheet-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="wosh-hh-sheet__close" aria-label={t("premiumLocker.backBtn")} onClick={onClose}>
          <Icon name="close" />
        </button>

        {view === "lockers" && (
          <>
            <span className="wosh-hh-sheet__badge">
              <Icon name="lock-open-outline" />
            </span>
            <h2 className="wosh-hh-sheet__title" id="wosh-hh-sheet-title">
              {t("premiumLocker.modalTitle")}
            </h2>
            {lockers.length === 0 ? (
              <div className="wosh-hh-sheet__empty">
                <Icon name="warning-outline" />
                {t("premiumLocker.noLockers")}
              </div>
            ) : (
              <div className="wosh-hh-sheet__lockers">
                {lockers.map((item, index) => (
                  <div className="wosh-hh-sheet__locker" key={item.id ?? index}>
                    <span className="wosh-hh-sheet__number">
                      <Icon name="cube-outline" />
                      {t("premiumLocker.lockerNumber")}
                      {item.number}
                    </span>
                    <span className="wosh-hh-sheet__code">
                      <span className="wosh-hh-sheet__code-label">{t("premiumLocker.accessCode")}</span>
                      <span className="wosh-hh-sheet__code-pill">{item.code}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {error && (
              <div className="wosh-hh-sheet__error" role="alert">
                {error}
              </div>
            )}
            <button type="button" className="wosh-hh-sheet__primary" disabled={busy} onClick={retrieve}>
              {busy ? t("premiumLocker.retrieving") : t("premiumLocker.retrieveBtn")}
            </button>
            <button type="button" className="wosh-hh-sheet__text" onClick={() => setView("report")}>
              <Icon name="alert-circle" />
              {t("premiumLocker.reportBtn")}
            </button>
          </>
        )}

        {view === "report" && (
          <>
            <h2 className="wosh-hh-sheet__title" id="wosh-hh-sheet-title">
              {t("premiumLocker.reportTitle")}
            </h2>
            <div className="wosh-hh-sheet__options">
              {["reportCode", "reportEmpty", "reportOther"].map((key) => (
                <button type="button" className="wosh-hh-sheet__option" key={key} onClick={() => setView("support")}>
                  {t(`premiumLocker.${key}`)}
                  <Icon name="chevron-forward" />
                </button>
              ))}
            </div>
            <button type="button" className="wosh-hh-sheet__text" onClick={() => setView("lockers")}>
              {t("premiumLocker.backBtn")}
            </button>
          </>
        )}

        {view === "support" && (
          <>
            <span className="wosh-hh-sheet__badge">
              <Icon name="help-circle-outline" />
            </span>
            <h2 className="wosh-hh-sheet__title" id="wosh-hh-sheet-title">
              {t("premiumLocker.supportTitle")}
            </h2>
            <p className="wosh-hh-sheet__message">{t("premiumLocker.supportMsg")}</p>
            <a className="wosh-hh-sheet__primary" href={SUPPORT_PHONE}>
              {t("premiumLocker.supportCallBtn")}
            </a>
            <button type="button" className="wosh-hh-sheet__text" onClick={() => setView("report")}>
              {t("premiumLocker.backBtn")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
  cards: state?.auth?.paymentId,
});

export default connect(mapStateToProps)(HomeHero);
